import React from 'react';
import {
    getProductImageUrlFromDefaultSwatch,
    getProductPageUrlFromDefaultSwatch,
    renderLabel,
    renderPrice,
    renderProductPlacementImage
} from '../../../components/MilProductComponent';
// } from '../../mill-products-list-page/components/mil-product.component';
import { getPayloadObject, getTelemetryAttributes, ITelemetryContent, onTelemetryClick } from '@msdyn365-commerce-modules/utilities';
import { IUnitOfMeasureConversionResponseMapEntity } from '../../../actions/DataServiceEntities.g';
import { ProductSearchResult } from '@msdyn365-commerce/retail-proxy';
import { IImageSettings } from '@msdyn365-commerce/core-internal';
import { getProductPageUrlSync } from '@msdyn365-commerce-modules/retail-actions';

export interface IMilSearchSuggestionProduct {
    imageSettings?: IImageSettings;
    savingsText?: string;
    freePriceText?: string;
    originalPriceText?: string;
    currentPriceText?: string;
    shouldDisplayPrice?: boolean;
    ratingAriaLabel?: string;
    telemetryContent?: ITelemetryContent;
    productUom?: IUnitOfMeasureConversionResponseMapEntity | undefined;
    context: any;
    product: ProductSearchResult;
    typeName: string;
    id: string;
    title: string | undefined;
    milItemNumProp: string | undefined;
}

export const MilSearchSuggestionProduct: React.FC<IMilSearchSuggestionProduct> = ({
    product,
    context,
    imageSettings,
    savingsText,
    freePriceText,
    originalPriceText,
    currentPriceText,
    shouldDisplayPrice,
    ratingAriaLabel,
    telemetryContent,
    productUom,
    typeName,
    id,
    title,
    milItemNumProp
}) => {
    let productUrl = getProductPageUrlSync(product?.Name ?? '', product?.RecordId ?? Number.MIN_VALUE, context.actionContext, undefined);
    const productImageUrlFromSwatch = getProductImageUrlFromDefaultSwatch(context, product) ?? product?.PrimaryImageUrl;
    const productPageUrlFromSwatch = getProductPageUrlFromDefaultSwatch(context, productUrl, product) ?? productUrl;
    const [productPageUrl, setProductPageUrl] = React.useState<string>(productPageUrlFromSwatch);
    const [productImageUrl, setProductImageUrl] = React.useState<string | undefined>(productImageUrlFromSwatch);
    React.useEffect(() => {
        setProductPageUrl(productPageUrlFromSwatch);
        setProductImageUrl(productImageUrlFromSwatch);
    }, [productUrl, productPageUrlFromSwatch, productImageUrlFromSwatch]);

    const payLoad = getPayloadObject('click', telemetryContent!, '', product.RecordId.toString());
    const attribute = getTelemetryAttributes(telemetryContent!, payLoad);

    let unitMultipliedPrice = Math.min(product.Price, product.BasePrice || 0);
    let priceUnitValue: number = 1;

    const surchargeAmount = productUom?.EcommSurchargeAmount || 0;
    const surchargeFactor = productUom?.EcommSurchargeFactor || 0;
    const isSurchargePercent = productUom?.IsEcommSurchargePercent || false;

    const calculateSurchargePerDefaultUnit = (unitPrice: number) => {
        if (surchargeAmount && surchargeFactor) {
            if (isSurchargePercent) {
                return unitPrice * (surchargeAmount / 100);
            } else {
                return surchargeAmount / surchargeFactor;
            }
        }
        return 0;
    };

    // const basePriceSurchargeAmountPerDefaultUnit = calculateSurchargePerDefaultUnit(unitMultipliedBasePrice ?? 0);
    const surchargeAmountPerDefaultUnit = calculateSurchargePerDefaultUnit(unitMultipliedPrice ?? 0);

    const priceUnit = productUom?.EcommPriceUnit || 1;
    if (priceUnit && unitMultipliedPrice) {
        unitMultipliedPrice = unitMultipliedPrice * priceUnit;
        priceUnitValue = priceUnit;
    }

    const unitMultipliedSurchargeAmount = priceUnitValue * surchargeAmountPerDefaultUnit;

    let unitDescription = '';
    const uomDesc = productUom?.DefaultDescription;
    if (uomDesc) {
        unitDescription = `${priceUnitValue && priceUnitValue > 1 ? priceUnitValue : ''} ${uomDesc}`;
    } else {
        unitDescription = `${product.DefaultUnitOfMeasure || ''}`;
    }
    return (
        <a
            href={productPageUrl}
            onClick={onTelemetryClick(telemetryContent!, payLoad, product.Name!)}
            aria-label={renderLabel(
                product.Name,
                // context.cultureFormatter.formatCurrency(product.Price),
                context.cultureFormatter.formatCurrency(unitMultipliedPrice),
                product.AverageRating,
                ratingAriaLabel
            )}
            {...attribute}
            className='msc-product'
        >
            <div className='msc-product__image'>
                {renderProductPlacementImage(
                    imageSettings,
                    context.request.gridSettings,
                    productImageUrl,
                    product.PrimaryImageUrl,
                    product.Name,
                    context
                )}
            </div>
            <div className='msc-product__details'>
                <h5 className='msc-product__title'>{title ? renderHighlightedTitle(title) : product.Name}</h5>
                <div className='msc-product-information'>
                    <div className='msc-product-info'>
                        <h5 className='msc-product-info__item-id'>{product.ItemId}</h5>
                        {shouldDisplayPrice && <h5 className='msc-product-info__price-per'>{`Price Per ${unitDescription}`}</h5>}
                    </div>
                    <div className='msc-product-extra'>
                        {shouldDisplayPrice && (
                            <div className='msc-product-info__price'>
                                {renderPrice(
                                    context,
                                    typeName,
                                    id,
                                    unitMultipliedSurchargeAmount + unitMultipliedPrice,
                                    unitMultipliedSurchargeAmount + unitMultipliedPrice,
                                    savingsText,
                                    freePriceText,
                                    originalPriceText,
                                    currentPriceText
                                )}
                            </div>
                        )}
                        {milItemNumProp && <p className='msc-product-info__mil-item'>{milItemNumProp}</p>}
                    </div>
                </div>
            </div>
        </a>
    );
};

const renderHighlightedTitle = (text: string) => {
    const parts = text.split(/(<em>|<\/em>)/g);
    let isEm = false;

    return parts.map((part, index) => {
        if (part === '<em>') {
            isEm = true;
            return null;
        }
        if (part === '</em>') {
            isEm = false;
            return null;
        }
        return isEm ? (
            <em key={index} className='matching-terms'>
                {part}
            </em>
        ) : (
            part
        );
    });
};

export default MilSearchSuggestionProduct;
